import { useState, useEffect, useContext } from 'react'
import { useT } from '../hooks'
import AuthContext from 'contexte/AuthContext'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import SearchUser from 'components/SearchUser/SearchUser'
import { launchError, launchSuccess } from 'components/notifications'
import { searchUsers } from 'services/fetchUsers'
import ConfirmModal from 'components/ConfirmModal/ConfirmModal'
import BBookEmpty from './BBookEmpty'
import BBookListToolbar, { BBookPager } from './BBookListToolbar'

// A redis user set managed from the page: mandatory A-book users, spread-exempt users. `i18n` is the key prefix.
const BBookUserSet = ({ i18n, fetchUsers, addUser, removeUser }) => {
  const { t } = useT()
  const { user } = useContext(AuthContext)

  const [page, setPage] = useState(0)
  const [fetchLimit, setFetchLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [users, setUsers] = useState([])
  const [searchResults, setSearchResults] = useState([])
  const [selectedUser, setSelectedUser] = useState({})
  const [adding, setAdding] = useState(false)
  const [toRemove, setToRemove] = useState(null)
  const [removing, setRemoving] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        setUsers(await fetchUsers(user, page, fetchLimit, search))
      } catch (e) {
        launchError(t(`notifications.b_book.${i18n}.fetch_error`))
      }
    }
    load()
  }, [page, fetchLimit, search])
  useEffect(() => setPage(0), [search, fetchLimit])

  const handleSearchUsers = async (value) => {
    if (!value.length) return setSearchResults([])
    try {
      setSearchResults(await searchUsers(user, value))
    } catch (e) {
      console.error(e)
    }
  }

  const handleAdd = async () => {
    if (!selectedUser.uuid) return
    try {
      setAdding(true)
      const created = await addUser(user, selectedUser.uuid)
      setUsers(prev => [created, ...prev.filter(({ uuid }) => uuid !== created.uuid)])
      setSelectedUser({})
      setSearchResults([])
      launchSuccess(t(`notifications.b_book.${i18n}.add_success`))
    } catch (e) {
      launchError(t(`notifications.b_book.${i18n}.add_error`))
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async () => {
    try {
      setRemoving(true)
      await removeUser(user, toRemove.uuid)
      setUsers(prev => prev.filter(({ uuid }) => uuid !== toRemove.uuid))
      launchSuccess(t(`notifications.b_book.${i18n}.remove_success`))
    } catch (e) {
      launchError(t(`notifications.b_book.${i18n}.remove_error`))
    } finally {
      setRemoving(false)
      setToRemove(null)
    }
  }
  const fullName = (item) => `${item.firstName} ${item.lastName}`

  const tbody = (users || []).map((item) => ({
    data: [
      fullName(item),
      item.email,
      <button className='b_book_row_button b_book_row_button_danger' aria-label={t(`pages.b_book.${i18n}.remove_label`, { name: fullName(item) })}
        onClick={(e) => { e.stopPropagation(); setToRemove(item) }}>
        {t(`pages.b_book.${i18n}.remove`)}
      </button>
    ],
    utils: { id: item.uuid }
  }))

  return (
    <section aria-labelledby={`b-book-${i18n}-title`}>
      <div className='b_book_title_component'>
        <h2 id={`b-book-${i18n}-title`}>{t(`pages.b_book.${i18n}.title`)}</h2>
      </div>
      <p className='b_book_hint'>{t(`pages.b_book.${i18n}.hint`)}</p>

      <div className='b_book_card'>
        <div className='b_book_add_row'>
          <SearchUser
            selectUser={setSelectedUser}
            selectedUser={selectedUser}
            users={searchResults}
            handleSearch={handleSearchUsers}
            label={t(`pages.b_book.${i18n}.search_placeholder`)}
          />
          <button className='b_book_action_button' disabled={!selectedUser.uuid || adding} aria-busy={adding || undefined} onClick={handleAdd}>
            {adding ? t('pages.b_book.saving') : t(`pages.b_book.${i18n}.add`)}
          </button>
        </div>

        {(tbody.length > 0 || search || page > 0) && (
          <BBookListToolbar searchLabel={t('pages.b_book.users_filter_label')} searchPlaceholder={t('pages.b_book.users_filter_placeholder')}
            search={search} setSearch={setSearch} limit={fetchLimit} setLimit={setFetchLimit} />
        )}
        {tbody.length || page > 0 ? (
          <div className="table-container">
            <div className="table-column">
              <Tableau
                thead={[t('pages.b_book.users_table.name'), t('pages.b_book.users_table.email'), t('pages.b_book.users_table.action')]}
                tbody={tbody}
              />
            </div>
            <BBookPager page={page} hasNext={tbody.length >= fetchLimit} onChange={setPage} />
          </div>
        ) : (
          <BBookEmpty title={t(search ? 'pages.b_book.users_empty_search' : `pages.b_book.${i18n}.empty`)} hint={search ? null : t(`pages.b_book.${i18n}.empty_hint`)} />
        )}
      </div>

      <ConfirmModal
        isOpen={!!toRemove}
        loading={removing}
        title={t(`pages.b_book.${i18n}.confirm_title`)}
        message={toRemove ? t(`pages.b_book.${i18n}.confirm_message`, { name: fullName(toRemove) }) : ''}
        confirmLabel={t(`pages.b_book.${i18n}.remove`)}
        cancelLabel={t('pages.b_book.positions.cancel')}
        onConfirm={handleRemove}
        onClose={() => setToRemove(null)}
      />
    </section>
  )
}

export default BBookUserSet
